/**
 * The supervisor's signed-in session: one emailed code, then every sheet sent to
 * that email opens without another (the dashboard and any other approval link).
 * The supervisor has no account, so the session holds only their email and the
 * server's session token, for 30 minutes on the server.
 *
 * It lives in localStorage, not sessionStorage, on purpose: each emailed link
 * opens in a new tab, and a per-tab store would ask for the code again on every
 * link. The server is the authority on expiry (an ended or foreign session is
 * refused and the code is asked for); `until` just stops this browser offering a
 * session the server has already ended. Storage can throw (private mode,
 * blocked); everything works without it, they just enter the code again.
 */
const KEY = 'external-supervisor-session'
const SESSION_MS = 30 * 60 * 1000

export function readDashboardSession() {
  try {
    const raw = window.localStorage.getItem(KEY)
    const saved = raw && JSON.parse(raw)
    if (!saved || !saved.session) return null
    if (saved.until && Date.now() > saved.until) {
      window.localStorage.removeItem(KEY)
      return null
    }
    return { email: saved.email, session: saved.session }
  } catch (e) {
    return null
  }
}

// `expiresAt` is the server's session_expires_at when there is one.
export function saveDashboardSession(email, session, expiresAt = null) {
  const parsed = expiresAt ? Date.parse(expiresAt) : NaN
  const until = Number.isNaN(parsed) ? Date.now() + SESSION_MS : parsed
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ email, session, until }))
  } catch (e) {}
}

export function clearDashboardSession() {
  try {
    window.localStorage.removeItem(KEY)
  } catch (e) {}
}

/**
 * "A code was already emailed" memory. The supervisor leaves the page to read
 * the code in their mail app, and a phone browser often reloads the tab on the
 * way back; without this they land on "Email me a code" again, get a second
 * code, and the first one stops working. Kept per link token (or per email on
 * the dashboard) until the code's own 10 minutes are up.
 */
const PENDING_KEY = 'external-supervisor-pending-code'
const CODE_MS = 10 * 60 * 1000

function readPendingAll() {
  try {
    const all = JSON.parse(window.localStorage.getItem(PENDING_KEY) || '{}')
    return all && typeof all === 'object' ? all : {}
  } catch (e) {
    return {}
  }
}

function writePendingAll(all) {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(all))
  } catch (e) {}
}

export function readPendingCode(id) {
  const saved = readPendingAll()[id]
  if (!saved || !saved.until || Date.now() > saved.until) return null
  return { sentTo: saved.sentTo || '' }
}

export function savePendingCode(id, sentTo, expiresAt = null) {
  const parsed = expiresAt ? Date.parse(expiresAt) : NaN
  const until = Number.isNaN(parsed) ? Date.now() + CODE_MS : parsed
  const all = readPendingAll()
  // Drop the finished ones so the store never grows.
  Object.keys(all).forEach((k) => {
    if (!all[k] || Date.now() > all[k].until) delete all[k]
  })
  all[id] = { sentTo, until }
  writePendingAll(all)
}

export function clearPendingCode(id) {
  const all = readPendingAll()
  delete all[id]
  writePendingAll(all)
}
